import type { SessionResponse, ConversationResponse, VisitorInfo } from '../types';

const STORAGE_KEY = 'envy_chat_session';
const VISITOR_ID_KEY = 'envy_chat_visitor_id';
const VISITOR_INFO_KEY = 'envy_chat_visitor_info';
const ACTIVE_CONVERSATION_KEY = 'envy_chat_active_conversation';

interface StoredSession {
  token: string;
  visitorId: string;
  expiresAt: string;
}

/**
 * Manages visitor session persistence using localStorage.
 * Falls back to in-memory storage if localStorage is unavailable (e.g., incognito).
 *
 * Persists:
 * - JWT session token (with expiry)
 * - Visitor ID (survives session expiry for returning visitor recognition)
 * - Visitor contact info (name, email, phone — survives page navigation)
 * - Active conversation (survives page navigation within session lifetime)
 */
export class SessionManager {
  private memoryStore: Map<string, string> = new Map();
  private storageAvailable: boolean;

  constructor() {
    this.storageAvailable = this.checkStorage();
  }

  private checkStorage(): boolean {
    try {
      const key = '__envy_storage_test__';
      localStorage.setItem(key, '1');
      localStorage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  }

  private getItem(key: string): string | null {
    if (this.storageAvailable) {
      return localStorage.getItem(key);
    }
    return this.memoryStore.get(key) ?? null;
  }

  private setItem(key: string, value: string): void {
    if (this.storageAvailable) {
      localStorage.setItem(key, value);
    } else {
      this.memoryStore.set(key, value);
    }
  }

  private removeItem(key: string): void {
    if (this.storageAvailable) {
      localStorage.removeItem(key);
    } else {
      this.memoryStore.delete(key);
    }
  }

  /** Get the stored visitor ID for returning visitor recognition */
  getVisitorId(): string | null {
    return this.getItem(VISITOR_ID_KEY);
  }

  /** Save the session from an API response */
  saveSession(session: SessionResponse): void {
    const stored: StoredSession = {
      token: session.token,
      visitorId: session.visitorId,
      expiresAt: session.expiresAt,
    };
    this.setItem(STORAGE_KEY, JSON.stringify(stored));
    this.setItem(VISITOR_ID_KEY, session.visitorId);
  }

  /** Retrieve a valid (non-expired) session token */
  getValidSession(): StoredSession | null {
    const raw = this.getItem(STORAGE_KEY);
    if (!raw) return null;

    try {
      const stored: StoredSession = JSON.parse(raw);
      const expiresAt = new Date(stored.expiresAt);

      // Add 5 min buffer before expiry
      const bufferMs = 5 * 60 * 1000;
      if (expiresAt.getTime() - bufferMs < Date.now()) {
        this.clearSession();
        return null;
      }

      return stored;
    } catch {
      this.clearSession();
      return null;
    }
  }

  /** Clear the session (but preserve visitorId and visitor info for returning visitor recognition) */
  clearSession(): void {
    this.removeItem(STORAGE_KEY);
    this.removeItem(ACTIVE_CONVERSATION_KEY);
  }

  // === Visitor Info Persistence ===

  /** Save visitor contact info (name, email, phone) to survive page navigation */
  saveVisitorInfo(info: Partial<VisitorInfo>): void {
    this.setItem(VISITOR_INFO_KEY, JSON.stringify(info));
  }

  /** Retrieve stored visitor contact info */
  getVisitorInfo(): Partial<VisitorInfo> | null {
    const raw = this.getItem(VISITOR_INFO_KEY);
    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /** Check if visitor has previously provided contact info */
  hasVisitorInfo(): boolean {
    const info = this.getVisitorInfo();
    // Name and email are required minimum
    return !!(info?.name && info?.email);
  }

  /** Clear visitor info (e.g., when visitor explicitly starts fresh) */
  clearVisitorInfo(): void {
    this.removeItem(VISITOR_INFO_KEY);
  }

  // === Active Conversation Persistence ===

  /** Save the active conversation so it survives page navigation */
  saveActiveConversation(conversation: ConversationResponse): void {
    this.setItem(ACTIVE_CONVERSATION_KEY, JSON.stringify(conversation));
  }

  /** Retrieve the active conversation (only valid within a valid session) */
  getActiveConversation(): ConversationResponse | null {
    // Only return active conversation if session is still valid
    if (!this.getValidSession()) {
      this.removeItem(ACTIVE_CONVERSATION_KEY);
      return null;
    }

    const raw = this.getItem(ACTIVE_CONVERSATION_KEY);
    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch {
      this.removeItem(ACTIVE_CONVERSATION_KEY);
      return null;
    }
  }

  /** Clear the active conversation (e.g., when conversation is resolved) */
  clearActiveConversation(): void {
    this.removeItem(ACTIVE_CONVERSATION_KEY);
  }

  // === Full Reset ===

  /** Clear everything — used for complete logout/reset */
  clearAll(): void {
    this.removeItem(STORAGE_KEY);
    this.removeItem(VISITOR_ID_KEY);
    this.removeItem(VISITOR_INFO_KEY);
    this.removeItem(ACTIVE_CONVERSATION_KEY);
  }
}
